import axios from "axios";

export type GrievanceType = "GRIEVANCE" | "SUGGESTION";
export type GrievanceCategory = "ACADEMIC" | "DISCIPLINE" | "FACILITY" | "OTHER";
export type GrievancePriority = "LOW" | "MEDIUM" | "HIGH";
export type GrievanceStatus = "OPEN" | "IN_PROGRESS" | "RESOLVED" | "CLOSED";

export interface GrievanceFilters {
  academicSessionId?: string;
  classId?: string;
  sectionId?: string;
  status?: GrievanceStatus;
  priority?: GrievancePriority;
  studentId?: string;
  type?: GrievanceType;
  fromDate?: string;
  toDate?: string;
}

export interface GrievanceListItem {
  id: string;
  ticketNumber: string;
  student: {
    id: string;
    firstName: string;
    lastName: string;
    admissionNumber: string;
  } | null;
  class:
    | {
        id: string;
        name: string;
      }
    | null;
  section:
    | {
        id: string;
        name: string;
      }
    | null;
  type: GrievanceType;
  category: GrievanceCategory;
  subject: string;
  priority: GrievancePriority;
  status: GrievanceStatus;
  createdAt: string;
}

export interface GrievanceComment {
  id: string;
  comment: string;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string;
  } | null;
}

export interface GrievanceDetail extends GrievanceListItem {
  description: string;
  resolution?: string | null;
  attachmentUrl?: string | null;
  session:
    | {
        id: string;
        name: string;
      }
    | null;
  assignedTo:
    | {
        id: string;
        firstName: string;
        lastName: string;
      }
    | null;
  comments: GrievanceComment[];
}

export interface GrievanceListResponse {
  data: GrievanceListItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
    stats: {
      total: number;
      open: number;
      inProgress: number;
      resolved: number;
      closed: number;
    };
  };
}

export interface CreateGrievancePayload {
  studentId: string;
  studentSessionRecordId?: string;
  academicSessionId?: string;
  classId?: string;
  sectionId?: string;
  type: GrievanceType;
  category: GrievanceCategory;
  subject: string;
  description: string;
  priority: GrievancePriority;
  attachmentUrl?: string;
}

export interface UpdateGrievancePayload {
  status?: GrievanceStatus;
  priority?: GrievancePriority;
  assignedToUserId?: string;
  resolution?: string;
}

export interface AddCommentPayload {
  comment: string;
}

export const grievanceApi = {
  async getAll(filters: GrievanceFilters, page = 1, limit = 10) {
    const response = await axios.get<GrievanceListResponse>(
      "/scholar-register/grievance",
      {
        params: {
          ...filters,
          page,
          limit,
        },
      },
    );
    return response.data;
  },

  async create(payload: CreateGrievancePayload) {
    const response = await axios.post<GrievanceDetail>(
      "/scholar-register/grievance",
      payload,
    );
    return response.data;
  },

  async getById(id: string) {
    const response = await axios.get<GrievanceDetail>(
      `/scholar-register/grievance/${id}`,
    );
    return response.data;
  },

  async update(id: string, payload: UpdateGrievancePayload) {
    const response = await axios.put<GrievanceDetail>(
      `/scholar-register/grievance/${id}`,
      payload,
    );
    return response.data;
  },

  async addComment(id: string, payload: AddCommentPayload) {
    const response = await axios.post<GrievanceDetail>(
      `/scholar-register/grievance/${id}/comment`,
      payload,
    );
    return response.data;
  },

  async remove(id: string) {
    await axios.delete(`/scholar-register/grievance/${id}`);
  },
};
